import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import styles from './EditName.module.scss';
import classNames from 'classnames';
import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export interface EditNameProps {
  value: string;
  placeholder?: string;
  onChange: (next: string) => void; // for controlled display value
  onCommit: (next: string) => void; // when save is confirmed
  className?: string;
  titleClassName?: string;
  inputClassName?: string;
  // Optional message shown under the input when the value is empty and
  // validation has been triggered (either via blur/Enter or triggerValidation()).
  // Defaults to 'Name is required'. Pass an empty string to suppress the message
  // entirely while keeping the red error styling (preserves old behavior).
  errorHelperText?: string;
}

export interface EditNameHandle {
  // Validates the current value and, if empty, surfaces the error state
  // and focuses the input. Returns true if valid, false otherwise.
  triggerValidation: () => boolean;
}

const EditName = forwardRef<EditNameHandle, EditNameProps>(
  (
    {
      value,
      placeholder,
      onChange,
      onCommit,
      className,
      titleClassName,
      inputClassName,
      errorHelperText = 'Name is required',
    },
    ref,
  ) => {
    const [editing, setEditing] = useState(!Boolean(value?.trim()));
    const [error, setError] = useState(false);
    const prevRef = useRef<string>(value || '');
    const inputRef = useRef<HTMLInputElement>(null);

    const save = () => {
      if (!value) {
        setError(true);
        inputRef.current?.focus();
        return;
      }
      prevRef.current = value;
      onCommit(value);
      setEditing(false);
    };

    const cancel = () => {
      onChange(prevRef.current);
      setEditing(false);
      setError(false);
    };

    useImperativeHandle(
      ref,
      () => ({
        triggerValidation: () => {
          if (!value?.trim()) {
            setEditing(true);
            setError(true);
            requestAnimationFrame(() => inputRef.current?.focus());
            return false;
          }
          return true;
        },
      }),
      [value],
    );

    // When the external value is empty, force edit mode and focus input so users clearly see it's required
    React.useEffect(() => {
      if (!value?.trim()) {
        setEditing(true);
        requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
      }
    }, [value]);

    return (
      <div className={classNames(styles.editName, className)}>
        {!editing ? (
          <div className={styles.displayRow}>
            <Typography
              className={classNames(styles.titleText, titleClassName)}
              title={value || placeholder}
              onClick={() => {
                setEditing(true);
                requestAnimationFrame(() => inputRef.current?.select());
              }}
            >
              {value || placeholder}
            </Typography>
            <Tooltip title="Edit name">
              <IconButton
                className={styles.editIcon}
                size="small"
                onClick={() => {
                  setEditing(true);
                  requestAnimationFrame(() => inputRef.current?.select());
                }}
                aria-label="Edit name"
              >
                <DriveFileRenameOutlineOutlinedIcon
                  style={{ color: 'var(--color-text-3)' }}
                />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <div className={styles.inputRow}>
            <TextField
              fullWidth
              inputRef={inputRef}
              color="primary"
              name="editName"
              placeholder={placeholder}
              value={value}
              className={classNames(styles.nameTextField, inputClassName, {
                // map error styling to MUI error prop for underline/border changes if needed
              })}
              error={error}
              helperText={error && errorHelperText ? errorHelperText : undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.value.length > 0) {
                  setError(false);
                }
                onChange(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  save();
                } else if (e.key === 'Escape') {
                  cancel();
                }
              }}
              onBlur={() => {
                if (!editing) return;
                if (!value?.trim()) {
                  return;
                }
                save();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={save}
                      disabled={!Boolean(value?.trim())}
                      aria-label="Save"
                    >
                      <CheckRoundedIcon
                        style={{ color: 'var(--color-text-3)' }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={cancel}
                      disabled={!Boolean(value?.trim())}
                      aria-label="Cancel"
                    >
                      <CloseRoundedIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'var(--color-accent)',
                  padding: '6px 8px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'var(--color-text-1)',
                  fontSize: '14px',
                },
              }}
            />
          </div>
        )}
      </div>
    );
  },
);

EditName.displayName = 'EditName';

export default EditName;